<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { getRandomColor } from 'dashboard/helper/labelColor';
import { useVuelidate } from '@vuelidate/core';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    prefillTitle: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      color: '#000',
      description: '',
      title: '',
      showOnSidebar: true,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  mounted() {
    this.color = getRandomColor();
    this.title = this.prefillTitle.toLowerCase();
    this.$nextTick(() => {
      this.$refs.titleInput?.focus?.();
    });
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async addLabel() {
      try {
        await this.$store.dispatch('labels/create', {
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
        });
        useAlert(this.$t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('LABEL_MGMT.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div
    class="label-modal flex flex-col h-auto w-full max-w-full overflow-x-hidden"
  >
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t('LABEL_MGMT.ADD.DESC')"
    />
    <form class="label-modal__form" @submit.prevent="addLabel">
      <div class="label-modal__body">
        <label
          class="label-modal__field"
          :class="{ 'label-modal__field--error': v$.title.$error }"
        >
          <span class="label-modal__label">
            {{ $t('LABEL_MGMT.FORM.NAME.LABEL') }}
          </span>
          <input
            ref="titleInput"
            v-model.trim="title"
            type="text"
            class="label-modal__input label-name--input"
            :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
            data-testid="label-title"
            @input="v$.title.$touch"
            @blur="v$.title.$touch"
          />
          <span v-if="v$.title.$error" class="label-modal__error">
            {{ labelTitleErrorMessage }}
          </span>
        </label>

        <label class="label-modal__field">
          <span class="label-modal__label">
            {{ $t('LABEL_MGMT.FORM.DESCRIPTION.LABEL') }}
          </span>
          <textarea
            v-model.trim="description"
            rows="3"
            class="label-modal__textarea"
            :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
            data-testid="label-description"
            @input="v$.description.$touch"
            @blur="v$.description.$touch"
          />
        </label>

        <div class="label-modal__settings">
          <div class="label-modal__card">
            <p class="label-modal__card-title">
              {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
            </p>
            <div class="label-modal__color-row">
              <woot-color-picker v-model="color" />
              <span class="label-modal__color-value">{{ color }}</span>
            </div>
          </div>

          <label class="label-modal__card label-modal__toggle-card">
            <input
              v-model="showOnSidebar"
              type="checkbox"
              class="label-modal__checkbox"
              :value="true"
            />
            <div>
              <p class="label-modal__card-title">
                {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
              </p>
            </div>
          </label>
        </div>
      </div>

      <div class="label-modal__actions">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          data-testid="label-submit"
          :label="$t('LABEL_MGMT.FORM.CREATE')"
          :disabled="v$.title.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.label-modal {
  background: #ffffff;
}

.label-modal__form {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 2rem 1rem;
}

.label-modal__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.label-modal__field {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  width: 100%;
}

.label-modal__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.label-modal__input,
.label-modal__textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.label-modal__input {
  min-height: 42px;
  padding: 0 0.875rem;
  text-transform: lowercase;
}

.label-modal__textarea {
  min-height: 96px;
  padding: 0.75rem 0.875rem;
  resize: vertical;
}

.label-modal__input:focus,
.label-modal__textarea:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.label-modal__field--error .label-modal__input,
.label-modal__field--error .label-modal__textarea {
  border-color: #ef4444;
}

.label-modal__error {
  font-size: 0.75rem;
  color: #ef4444;
}

.label-modal__settings {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.875rem;
}

.label-modal__card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
  min-height: 88px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
}

.label-modal__toggle-card {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
}

.label-modal__card-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.label-modal__color-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.label-modal__color-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
  word-break: break-all;
}

.label-modal__checkbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: #111827;
}

.label-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;
}

@media (max-width: 640px) {
  .label-modal__form {
    padding: 0 1rem 0.875rem;
  }

  .label-modal__settings {
    grid-template-columns: 1fr;
  }
}

::v-deep(.modal-header),
::v-deep(.modal-body),
::v-deep(.modal-footer),
::v-deep(.modal-container) {
  background: #ffffff !important;
}

::v-deep(.colorpicker--selected) {
  width: 40px;
  height: 40px;
  margin-bottom: 0;
  border-radius: 10px;
  border-color: #d1d5db;
}

::v-deep(.colorpicker--dropdown) {
  border-color: #e5e7eb;
  background: #ffffff;
}

::v-deep(.preset-title) {
  color: #111827 !important;
}

::v-deep(.preset-item) {
  border-color: #e5e7eb !important;
}

::v-deep(input::placeholder),
::v-deep(textarea::placeholder) {
  color: #9ca3af;
}
</style>
